import { telegramStartPath } from "@vekha/contracts";
import { applyTheme, themeOfTelegramParams, themeToken, type Theme } from "./theme";

/**
 * Vekha inside Telegram (Mini App of Yana's and Arkadiy's bots, 04.11.2026). Telegram opens the page with its launch data
 * in the address hash (#tgWebAppData=…); the app keeps it for the tab (sessionStorage: the router drops the hash on the
 * first navigation, a reload must still sign in), marks <html class="tg"> and only then loads telegram-web-app.js —
 * an ordinary browser loads nothing of Telegram.
 */
export const TELEGRAM_INIT_DATA_KEY = "vekha.telegram.initData";
const START_APPLIED_KEY = "vekha.telegram.startApplied";
const THEME_PARAMS_KEY = "vekha.telegram.themeParams";
const SCRIPT_URL = "https://telegram.org/js/telegram-web-app.js";

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;
type Launch = Readonly<{ initData: string; startParam: string | null; launchHash: string | null }>;

/** The launch data of this tab: from the address when Telegram has just opened it, else the one kept for the tab. */
export function readTelegramLaunch(hash: string, storage: Storage | null): Launch | null {
  const params = new URLSearchParams(hash.replace(/^#/u, ""));
  const fresh = params.get("tgWebAppData");
  let initData = fresh;
  try {
    if (fresh) storage?.setItem(TELEGRAM_INIT_DATA_KEY, fresh);
    else initData = storage?.getItem(TELEGRAM_INIT_DATA_KEY) ?? null;
  } catch {
    // A WebView without storage: the hash of this load is all there is.
  }
  if (!initData) return null;
  const signed = new URLSearchParams(initData);
  return {
    initData,
    // The signed start parameter first: the address one is the same value, unsigned.
    startParam: signed.get("start_param") ?? params.get("tgWebAppStartParam"),
    launchHash: fresh ? signed.get("hash") : null,
  };
}

/**
 * Direct Link Mini App (t.me/<bot>/<app>?startapp=report_…): the screen it names, once per launch — a reload of the
 * same launch stays where the person went since. Only the kinds of telegramStartPath, never a free address.
 */
export function telegramStartTarget(launch: Launch | null, storage: Storage | null): string | null {
  if (!launch?.launchHash) return null;
  const path = telegramStartPath(launch.startParam);
  if (!path) return null;
  try {
    if (storage?.getItem(START_APPLIED_KEY) === launch.launchHash) return null;
    storage?.setItem(START_APPLIED_KEY, launch.launchHash);
  } catch {
    // Without storage every reload of this launch opens the named screen again; harmless.
  }
  return path;
}

function safeSessionStorage(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

const launch = typeof window === "undefined" ? null : readTelegramLaunch(window.location.hash, safeSessionStorage());

/** initData of this tab, or null outside Telegram. */
export function telegramInitData(): string | null {
  return launch?.initData ?? null;
}

let linkPending = false;
/** The Telegram id of this launch is not linked yet: the login page says so, and a login links it (auth-context). */
export function markTelegramLinkPending(value: boolean): void { linkPending = value; }
export function telegramLinkPending(): boolean { return linkPending && launch !== null; }

type TelegramBackButton = { show(): void; hide(): void; onClick(callback: () => void): void; offClick(callback: () => void): void };
export type TelegramWebApp = {
  version: string;
  ready(): void;
  expand(): void;
  isVersionAtLeast(version: string): boolean;
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  colorScheme?: Theme;
  onEvent?(event: "themeChanged", callback: () => void): void;
  BackButton?: TelegramBackButton;
};

let webApp: Promise<TelegramWebApp | null> | null = null;

/** Telegram.WebApp once its script has loaded and the app is set up; null outside Telegram or when the script fails. */
export function telegramWebApp(): Promise<TelegramWebApp | null> {
  if (webApp) return webApp;
  if (!launch) return (webApp = Promise.resolve(null));
  webApp = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      const app = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp ?? null;
      if (app) configure(app);
      resolve(app);
    };
    script.onerror = () => resolve(null);
    document.head.append(script);
  });
  return webApp;
}

function configure(app: TelegramWebApp): void {
  app.ready();
  app.expand();
  if (app.isVersionAtLeast("7.7")) app.disableVerticalSwipes?.();
  // The theme follows Telegram's (light or dark): the page's tokens switch, and Telegram's top bar, the background behind the
  // page and the bottom bar take the canvas and the surface of that theme, so the page never sits in a frame of another colour.
  const paint = () => {
    if (app.colorScheme === "light" || app.colorScheme === "dark") applyTheme(app.colorScheme);
    if (!app.isVersionAtLeast("6.9")) return;
    app.setHeaderColor?.(themeToken("--canvas", "#F4F4FA"));
    app.setBackgroundColor?.(themeToken("--canvas", "#F4F4FA"));
    if (app.isVersionAtLeast("7.10")) app.setBottomBarColor?.(themeToken("--surface", "#FFFFFF"));
  };
  paint();
  app.onEvent?.("themeChanged", paint);
}

/** Before the app's first read (main.tsx): the class for Telegram's styles, the screen of the start parameter. */
export function startTelegram(): void {
  if (!launch) return;
  document.documentElement.classList.add("tg");
  // Telegram's theme before the first paint: from the launch parameters (kept for the tab, a reload has no hash).
  const storage = safeSessionStorage();
  let themeParams = new URLSearchParams(window.location.hash.replace(/^#/u, "")).get("tgWebAppThemeParams");
  try {
    if (themeParams) storage?.setItem(THEME_PARAMS_KEY, themeParams);
    else themeParams = storage?.getItem(THEME_PARAMS_KEY) ?? null;
  } catch {
    // Without storage a reload follows the system until Telegram's script answers.
  }
  applyTheme(themeOfTelegramParams(themeParams));
  const target = telegramStartTarget(launch, safeSessionStorage());
  // The hash stays: telegram-web-app.js reads its launch parameters from it when it runs.
  if (target) window.history.replaceState(null, "", `${target}${window.location.hash}`);
  void telegramWebApp();
}
